import { RevisionStatus } from './autogenerated-coating';

export interface RevisionStatusItem {
  text: string;
  status: RevisionStatus;
  displayName: string;
  cssClass: string;
}

export class RevisionStatusHelper {

  static readonly STATUS_ITEMS: RevisionStatusItem[] = [
    {
      text: 'draft',
      status: RevisionStatus.DRAFT,
      displayName: 'Draft',
      cssClass: 'draft-status'
    },
    {
      text: 'in review',
      status: RevisionStatus.REVIEW,
      displayName: 'In Review',
      cssClass: 'review-status'
    },
    {
      text: 'in review with rejections',
      status: RevisionStatus.REVIEW_WITH_REJECTIONS,
      displayName: 'In Review (Contains Rejections)',
      cssClass: 'review-rejected-status'
    },
    {
      text: 'in review with pending comments',
      status: RevisionStatus.REVIEW_WITH_PENDING_COMMENTS,
      displayName: 'In Review (Pending Comments)',
      cssClass: 'review-pending-comments-status'
    },
    {
      // Persisted as APPROVAL (2) in the DB, surfaced as "Ready to Publish" in the UI.
      text: 'ready to publish',
      status: RevisionStatus.APPROVAL,
      displayName: 'Ready to Publish',
      cssClass: 'ready-to-publish-status'
    },
    {
      text: 'ready to publish with pending comments',
      status: RevisionStatus.APPROVAL_WITH_PENDING_COMMENTS,
      displayName: 'Ready (Pending Comments)',
      cssClass: 'approval-pending-comments-status'
    },
    {
      text: 'published',
      status: RevisionStatus.PUBLISHED,
      displayName: 'Published',
      cssClass: 'published-status'
    },
    {
      text: 'retired',
      status: RevisionStatus.RETIRED,
      displayName: 'Retired',
      cssClass: 'retired-status'
    },
    {
      text: 'rejected',
      status: RevisionStatus.REJECTED,
      displayName: 'Rejected',
      cssClass: 'rejected-status'
    }
  ];

  static getDisplayName(status: RevisionStatus | number): string {
    const item = this.STATUS_ITEMS.find(item => item.status === status);
    return item ? item.displayName : `Status ${status}`;
  }

  static getCssClass(status: RevisionStatus | number): string {
    const item = this.STATUS_ITEMS.find(item => item.status === status);
    return item ? item.cssClass : 'unknown-status';
  }

  static getInternalText(status: RevisionStatus | number): string {
    const item = this.STATUS_ITEMS.find(item => item.status === status);
    return item ? item.text : `status_${status}`;
  }

  static getWorkflowItems(visibilityConfig: {
    isStatus_0Visible?: boolean,
    isStatus_1Visible?: boolean,
    isStatus_2Visible?: boolean,
    isStatus_3Visible?: boolean,
    isStatus_100Visible?: boolean,
    isStatus_101Visible?: boolean
  } = {}): WorkflowItem[] {
    // Only the main lifecycle statuses appear in the workflow bar (pseudo-statuses are excluded).
    const mainStatuses = this.STATUS_ITEMS.filter(item =>
      [RevisionStatus.DRAFT, RevisionStatus.REVIEW, RevisionStatus.APPROVAL, RevisionStatus.PUBLISHED,
       RevisionStatus.RETIRED, RevisionStatus.REJECTED].includes(item.status)
    );
    return mainStatuses.map(item => ({
      text: item.text,
      status: item.status,
      displayName: item.displayName,
      cssClass: item.cssClass,
      isActive: false,
      isVisible: visibilityConfig[`isStatus_${item.status}Visible` as keyof typeof visibilityConfig] ?? true,
      finished: false,
      isFailed: false
    }));
  }
}

export interface WorkflowItem {
  text: string;
  status: RevisionStatus;
  displayName: string;
  cssClass: string;
  finished?: boolean;
  isFailed?: boolean;
  isActive?: boolean;
  isVisible?: boolean;
}
